import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatBRL, API } from "@/lib/api";
import { CheckCircle2, MessageCircle, Download, Copy, Check } from "lucide-react";

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [wa, setWa] = useState("");
  const [pix, setPix] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/me/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => {});
    api.get("/content").then(({ data }) => setWa(data?.whatsapp_number || "")).catch(() => {});
    api.get(`/me/orders/${id}/pix`).then(({ data }) => setPix(data)).catch(() => setPix({ unavailable: true }));
  }, [id]);

  if (!order) return <div className="p-20 text-center text-[#49392F]/70">Carregando...</div>;

  const token = localStorage.getItem("duo_token");
  const receiptUrl = `${API}/me/orders/${order.id}/receipt`;

  const downloadReceipt = async () => {
    try {
      const r = await fetch(receiptUrl, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `recibo-${order.order_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };

  const copyPix = async () => {
    if (!pix?.payload) return;
    try { await navigator.clipboard.writeText(pix.payload); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* noop */ }
  };

  const waLink = wa
    ? `https://wa.me/${wa.replace(/\D/g, "")}?text=${encodeURIComponent(`Olá! Fiz o pedido ${order.order_number} no site da Duo Áurea.`)}`
    : null;

  const total = order.total || order.subtotal || 0;

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-16 text-center" data-testid="order-success-page">
      <CheckCircle2 size={56} className="mx-auto text-[#C2A064]" />
      <h1 className="font-display text-4xl text-[#49392F] mt-6">Pedido recebido!</h1>
      <p className="mt-4 text-[14px] text-[#49392F]/80 leading-relaxed">
        Seu pedido <strong data-testid="order-number">{order.order_number}</strong> foi registrado com sucesso.
      </p>

      {pix && pix.payload && (
        <div className="mt-10 bg-white border border-[#C2A064]/30 p-8 text-left" data-testid="pix-section">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#C2A064]">Pague agora com Pix</div>
              <div className="font-display text-2xl text-[#49392F] mt-1">{formatBRL(pix.amount)}</div>
            </div>
            <div className="text-[11px] text-[#49392F]/70">Recebedor: {pix.merchant_name}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-center">
            <div className="bg-white border border-[#E8DCCB] p-3">
              <img
                src={`${API}/me/orders/${order.id}/pix.png`}
                alt="QR Code Pix"
                className="w-full h-auto"
                style={{ imageRendering: "pixelated" }}
                data-testid="pix-qr"
              />
            </div>
            <div>
              <p className="text-[13px] text-[#49392F]/80 mb-3 leading-relaxed">
                Abra o app do seu banco, escolha pagar com Pix e escaneie o QR Code — ou copie e cole o código:
              </p>
              <div className="bg-[#F8F5EF] border border-[#E8DCCB] p-3 text-[11px] text-[#49392F]/80 break-all font-mono max-h-24 overflow-y-auto">
                {pix.payload}
              </div>
              <button onClick={copyPix} className="duo-btn duo-btn-primary mt-3 w-full md:w-auto" data-testid="pix-copy-button">
                {copied ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar código Pix</>}
              </button>
            </div>
          </div>
          <p className="text-[11px] text-[#49392F]/60 mt-4">
            Após o pagamento, enviaremos a confirmação por WhatsApp e e-mail. Se preferir pagar depois do envio do frete, aguarde nosso contato.
          </p>
        </div>
      )}

      <div className="mt-10 bg-white border border-[#C2A064]/20 p-8 text-left">
        <div className="flex justify-between text-[13px] text-[#49392F] mb-2">
          <span>Status</span><span>{order.status_label}</span>
        </div>
        <div className="hairline my-4" />
        {order.items.map((i, idx) => (
          <div key={idx} className="flex justify-between text-[13px] text-[#49392F] mb-2">
            <span>{i.quantity}x {i.name}</span>
            <span>{formatBRL(i.subtotal)}</span>
          </div>
        ))}
        {order.shipping_cost ? (
          <div className="flex justify-between text-[13px] text-[#49392F] mb-2">
            <span>Frete {order.shipping_option?.name ? `(${order.shipping_option.name})` : ""}</span>
            <span>{formatBRL(order.shipping_cost)}</span>
          </div>
        ) : null}
        <div className="hairline my-4" />
        <div className="flex justify-between text-[15px] font-medium text-[#49392F]">
          <span>Total</span><span>{formatBRL(total)}</span>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
        <button onClick={downloadReceipt} className="duo-btn duo-btn-primary" data-testid="download-receipt-button">
          <Download size={16} /> Baixar recibo PDF
        </button>
        {waLink && (
          <a href={waLink} target="_blank" rel="noreferrer" className="duo-btn duo-btn-gold">
            <MessageCircle size={16} /> Falar no WhatsApp
          </a>
        )}
        <Link to="/conta?tab=pedidos" className="duo-btn duo-btn-secondary">Meus pedidos</Link>
      </div>
    </div>
  );
}
